import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './styles/tokens.css';
import './index.css';
import './styles/wireframe.css';
import App from './App.jsx';
import AppErrorFallback from './components/shared/AppErrorFallback';

const isSentryEnvironment = import.meta.env.MODE === 'uat' || import.meta.env.MODE === 'production';
const sentryDsnByMode = {
  uat:
    import.meta.env.VITE_SENTRY_DSN_UAT ||
    'https://9ed94db44f728bcc02939315f3789e47@o4511217093509120.ingest.de.sentry.io/4511355400945744',
  production:
    import.meta.env.VITE_SENTRY_DSN_PROD ||
    'https://b64bfaecd6c0b29c14bea51cf04e815d@o4511217093509120.ingest.de.sentry.io/4511355524874320',
};
const sentryDsn =
  import.meta.env.VITE_SENTRY_DSN ||
  (isSentryEnvironment ? sentryDsnByMode[import.meta.env.MODE] : '');

if (typeof window !== 'undefined') {
  const persistedTheme = window.localStorage.getItem('wb-theme') || 'light';
  document.documentElement.dataset.theme = persistedTheme;
}

const canonicalHost = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

if (
  typeof window !== 'undefined' &&
  import.meta.env.PROD &&
  canonicalHost &&
  window.location.hostname !== canonicalHost
) {
  const isFirebaseHost = (host) => host.endsWith('.web.app') || host.endsWith('.firebaseapp.com');
  const isFirebaseAuthPath = window.location.pathname.startsWith('/__/auth/');
  const isPreviewChannelHost = window.location.hostname.includes('--');

  if (
    !isFirebaseAuthPath &&
    !isPreviewChannelHost &&
    isFirebaseHost(window.location.hostname) &&
    isFirebaseHost(canonicalHost)
  ) {
    const nextUrl = `${window.location.protocol}//${canonicalHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(nextUrl);
  }
}

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
